import styled from "@emotion/styled";
import { Form, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { getLanguage } from "auth-provider";
import { ReactNode, useEffect, useState, useCallback } from "react";
import { LabelType } from "screens/register/page-header";
import drop_arrow_down from "assets/drop_arrow_down.png";
import {
  BtnText,
  formPlaceholder,
  VerifyTipConfig,
} from "screens/register/contant";
import {
  AUTH_API_URL,
  sendMessageToParent,
  StorageMobile,
  useProjectModal,
} from "screens/register/util";
import { DrawForm } from "./form-utils";
import { getErrorMsg, useHttp } from "utils/http";
import { useQueryClient } from "react-query";
import { UserPhoneQueryKey } from "utils/oauth";
import parsePhoneNumberFromString, {
  isValidPhoneNumber,
} from "libphonenumber-js";
import { defaultHeaders } from "utils/request";
import "../register/customize.css";
import {
  MIX_PANEL_EVENT,
  trackMixPanel,
  useTrackCommonData,
} from "mixpanel/mixpanelTrackUtil";
import { getFormatNumber, slicePhoneNumber } from "utils";
import { BirthdayEntry } from "screens/register/birthdayEntry";
import dayjs from 'dayjs';

export const JoinMemberForm = ({
  setPhone,
  setHeight,
  userStatus,
  userConVisible,
  checkedFailed,
  refreshNum,
  onboardingForTwilio,
  onboardingForTwilioCloseCallback,
  children,
}: {
  setPhone: (phone: string) => void;
  setHeight: (height: string) => void;
  checkedFailed: (phoneNumber: string) => void;
  userStatus: number;
  userConVisible: boolean;
  refreshNum: number;
  onboardingForTwilio?: boolean;
  onboardingForTwilioCloseCallback?: (e: any) => void;
  children: ReactNode;
}) => {
  const client = useHttp();
  const lang = getLanguage() as LabelType;

  const [btnDisabled, setIsEnable] = useState(true);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", birthday: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [birthdayStr, setBirthdayStr] = useState("");
  const [openBirthdayEntry, setOpenBirthdayEntry] = useState(false);

  const consentHeight = 4.4;

  const errorMsgNotify = useCallback(
    (description: string) => {
      if (lang === "en") {
        setErrorMsg(description);

        return;
      }
      // 中文提示
      if (description === VerifyTipConfig.phoneError.en) {
        setErrorMsg(VerifyTipConfig.phoneError.zh);
      } else if (description === VerifyTipConfig.phoneError2.en) {
        setErrorMsg(VerifyTipConfig.phoneError2.zh);
      } else {
        setErrorMsg(description);
      }
    },
    [lang]
  );

  useEffect(() => {
    if (errorMsg === VerifyTipConfig.phoneError.en) {
      setHeight(`${32 + consentHeight}rem`);
    } else if (errorMsg === VerifyTipConfig.phoneError2.en) {
      setHeight(`${30 + consentHeight}rem`);
    } else if (errorMsg) {
      setHeight(`${29 + consentHeight}rem`);
    } else {
      setHeight(`${24.4 + consentHeight}rem`);
    }
  }, [errorMsg, setHeight]);

  const handleChange = (e: any, type: keyof typeof formData) => {
    const inputVal = e.target.value.trim();
    setFormData({ ...formData, [type]: inputVal });
    // 计算
    const finalValObj = Object.assign({}, formData, { [type]: inputVal });
    setIsEnable(!finalValObj.name || !finalValObj.phone);
    if (type === "phone" && inputVal) {
      setErrorMsg("");
    }
  };

  const onOpenBirthdayEntry = useCallback(() => {
    setOpenBirthdayEntry(true);
  }, []);

  const onBirthdayConfirm = useCallback((birthday: dayjs.Dayjs | null) => {

    if (birthday) {
      let birthdayStr = birthday?.format('MM-DD-YYYY') || '';
      setBirthdayStr(birthdayStr);
      setFormData({ ...formData, 'birthday': birthdayStr });
    }
    setOpenBirthdayEntry(false);
  }, [formData]);

  const onBirthdayCancel = useCallback(() => {
    setOpenBirthdayEntry(false);
  }, []);


  const { open } = useProjectModal();
  const queryClient = useQueryClient();

  const trackCommonData = useTrackCommonData();

  const onFinish = (values: any) => {
    if (btnDisabled || isLoading) return;
    if (!isValidPhoneNumber(values.phone, "US")) {
      setErrorMsg(VerifyTipConfig.error[lang]);
      setIsEnable(true);
      return;
    }

    if (userStatus !== 1 && userStatus !== 3) {
      checkedFailed(getFormatNumber(values.phone) + "");
      return;
    }
    //如果是为了Twilio onboarding，那么直接返回
    if (onboardingForTwilio) {
      onboardingForTwilioCloseCallback && onboardingForTwilioCloseCallback({});
      return;
    };

    sendPhone();
  };

  const sendPhone = useCallback(() => {
    const values = formData;
    const numWithPrefix = getFormatNumber(values.phone) + "";
    // 验证手机号通过
    setErrorMsg("");
    setIsLoading(true);
    trackMixPanel(MIX_PANEL_EVENT.REGISTER_SEND_PHONE_NAME, trackCommonData);
    sendMessageToParent(
      {
        type: "mixpanel",
        data: { eventName: MIX_PANEL_EVENT.REGISTER_SEND_PHONE_NAME },
      },
      trackCommonData.source
    );
    client(`${AUTH_API_URL}/api/${lang}/v1/visitors/verification_code`, {
      method: "post",
      headers: defaultHeaders,
      data: {
        phone_number: numWithPrefix,
        marketing_source: "RestaurantTech_Loyalty",
      },
    })
      .then((data) => {
        setIsLoading(false);
        if (!data && !data.success) {
          message.error(VerifyTipConfig.msgError[lang]);
          return;
        }
        trackMixPanel(
          MIX_PANEL_EVENT.REGISTER_SEND_PHONE_NAME_SUCCESS,
          trackCommonData
        );
        message.success(VerifyTipConfig.msg[lang]);
        queryClient.invalidateQueries(UserPhoneQueryKey);
        queryClient.setQueryData(UserPhoneQueryKey, {
          name: values.name,
          phone: numWithPrefix,
          email: values.email || '',
          birthday: values.birthday || '',
        });
        setPhone(
          parsePhoneNumberFromString(`${values.phone}`, {
            defaultCountry: "US",
          })?.formatNational() || ""
        );
        StorageMobile("set", numWithPrefix);
        open("verify", "register");
      })
      .catch((e) => {
        setIsLoading(false);
        const msg = getErrorMsg(e);
        errorMsgNotify(msg);
        trackMixPanel(MIX_PANEL_EVENT.REGISTER_SEND_PHONE_NAME_FAILED, {
          errorMsg: msg,
          mNo: slicePhoneNumber(numWithPrefix),
          errorApi: "api/{lang}/v1/visitors/verification_code",
          ...trackCommonData,
        });
      });
  }, [
    client,
    errorMsgNotify,
    formData,
    lang,
    open,
    queryClient,
    setPhone,
    trackCommonData,
  ]);

  useEffect(() => {
    setIsLoading(userStatus === 2);
  }, [userStatus]);

  useEffect(() => {
    if (!refreshNum) {
      return;
    }
    sendPhone();
    // eslint-disable-next-line
  }, [refreshNum]);

  const [formRef] = useForm();
  useEffect(() => {
    const inputIns = formRef.getFieldInstance("name");
    inputIns?.focus();
  }, [formRef]);

  return (
    <>
      <DrawForm
        form={formRef}
        layout={"vertical"}
        onFinish={onFinish}
        initialValues={formData}
      >
        {/* 加入会员 */}
        <Form.Item style={{ marginBottom: "1.6rem" }} name={"name"}>
          <input
            name={"name"}
            placeholder={formPlaceholder.join.name[lang]}
            autoComplete={"off"}
            className={"iptClasses commonIptClass"}
            maxLength={20}
            autoFocus
            onChange={(e) => handleChange(e, "name")}
          ></input>
        </Form.Item>


        <Form.Item style={{ marginBottom: "1.6rem" }} name={"phone"}>
          <div style={{ position: "relative" }}>
            <PrefixInput>+1</PrefixInput>
            <input
              type={"number"}
              name={"phone"}
              value={formData.phone}
              maxLength={20}
              placeholder={formPlaceholder.join.phone[lang]}
              autoComplete={"off"}
              style={{ paddingLeft: "6rem" }}
              className={"iptClasses commonIptClass"}
              onChange={(e) => handleChange(e, "phone")}
            ></input>
          </div>
        </Form.Item>

        <Form.Item style={{ marginBottom: "1.6rem" }} name={"email"}>
          <input
            name={"email"}
            placeholder={formPlaceholder.join.email[lang]}
            autoComplete={"off"}
            className={"iptClasses commonIptClass"}
            maxLength={40}
            autoFocus
            onChange={(e) => handleChange(e, "email")}
          ></input>
        </Form.Item>

        <Form.Item style={{ marginBottom: "1.6rem" }} name={"birthday"}>
          <input
            name={"birthday"}
            value={birthdayStr}
            readOnly={true}
            placeholder={formPlaceholder.join.birthday[lang]}
            autoComplete={"off"}
            style={{ paddingRight: "6rem" }}
            className={"iptClasses commonIptClass"}
            onClick={onOpenBirthdayEntry}
          ></input>
          <PostfixInput style={{ borderLeft: 0 }}>
            <img src={drop_arrow_down} alt="img" style={{ width: "24px" }} />
          </PostfixInput>
        </Form.Item>

        {errorMsg && <TipMsg>{errorMsg}</TipMsg>}
        <Form.Item style={{ marginTop: "1.6rem", textAlign: "center" }}>
          <SubmitButton
            type={"submit"}
            lang={lang}
            isDisabled={!!isLoading || btnDisabled}
          >
            {!isLoading
              ? formPlaceholder.join.btn[lang]
              : BtnText.btnSending[lang]}
          </SubmitButton>
        </Form.Item>
        {children}
      </DrawForm>
      <BirthdayEntry open={openBirthdayEntry} onConfirm={onBirthdayConfirm} onCancel={onBirthdayCancel} />
    </>
  );
};

const PrefixInput = styled.div`
  position: absolute;
  z-index: 99;
  top: 1.2rem;
  left: 1.8rem;
  color: #1d1b2e;
  padding-right: 1.7rem;
  border-right: 0.5px solid #c0c2cc;
  font-size: 1.4rem;
  line-height: 2rem;
`;

const PostfixInput = styled.div`
  position: absolute;
  z-index: 99;
  top: 1.2rem;
  right: 1.8rem;
  color: #1d1b2e;
  padding-left: 1.7rem;
  border-left: 0.5px solid #c0c2cc;
  font-size: 1.4rem;
  line-height: 2rem;
`;


export const TipMsg = styled.div`
  font-size: 1.4rem;
  line-height: 1.8rem;
  text-align: center;
  color: #e00051;
`;

export const SubmitButton = styled.button<{
  isDisabled: Boolean;
  lang: LabelType;
}>`
  background: ${(props) => (props.isDisabled ? "#C0C2CC" : "#ed2668")};
  font-weight: ${(props) => (props.lang === "en" ? 700 : 600)};
  font-size: 1.8rem;
  border-radius: 2.2rem;
  height: 4.4rem;
  line-height: 1;
  border: none;
  color: #fff;
  width: 100%;
`;
